import Head from 'next/head'
import { ArticleJsonLd, BreadcrumbJsonLd, FAQPageJsonLd } from 'next-seo'

import { STOREFRONT_NAME } from '@/shared/consts'
import type { Article } from '@/slices/blog/mocks/articles'
import type { FAQBlock } from '@/slices/blog/mocks/articles/types'
import type { Author } from '@/slices/blog/mocks/authors'

const SITE_URL = 'https://www.pixieworldstore.com'

interface BlogPostSeoProps {
  article: Article
  author: Author
}

const wordCount = (article: Article): number => {
  const stripMd = (s: string) => s.replace(/[`*_~#>[\]()!-]/g, ' ')
  const text = article.blocks
    .map((b) => {
      if (b.type === 'markdown') return stripMd(b.content)
      if (b.type === 'callout') return `${b.title ?? ''} ${b.content}`
      if (b.type === 'key-takeaway') return `${b.title ?? ''} ${b.points.join(' ')}`
      if (b.type === 'pull-quote') return b.quote
      if (b.type === 'cta') return `${b.title} ${b.description ?? ''}`
      if (b.type === 'faq') return b.questions.map((q) => `${q.question} ${q.answer}`).join(' ')
      if (b.type === 'numbered-steps') return b.steps.map((s) => `${s.title} ${s.description}`).join(' ')
      if (b.type === 'stat-highlight') return `${b.stat} ${b.description}`

      return ''
    })
    .join(' ')

  return text.split(/\s+/).filter(Boolean).length
}

// Resolve a possibly-relative image path to an absolute URL for OG/JSON-LD.
const absoluteUrl = (maybePath: string | undefined): string | undefined => {
  if (!maybePath) return undefined
  if (/^https?:\/\//i.test(maybePath)) return maybePath

  return `${SITE_URL}${maybePath.startsWith('/') ? '' : '/'}${maybePath}`
}

export function BlogPostSeo({ article, author }: BlogPostSeoProps) {
  const title = article.metaTitle || article.title
  const seoTitle = `${title} | ${STOREFRONT_NAME}`
  const description = article.metaDescription || article.excerpt
  const canonical = article.canonical || `${SITE_URL}/blog/${article.slug}`
  const ogImage = absoluteUrl(article.ogImage || article.heroImage || article.image)
  const ogImageAlt = article.ogImageAlt || title
  const twitterImage = absoluteUrl(article.twitterImage) || ogImage

  const faqBlock = article.blocks.find((b): b is FAQBlock => b.type === 'faq')
  const articleWordCount = wordCount(article)

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content={STOREFRONT_NAME} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {ogImage && <meta property="og:image:alt" content={ogImageAlt} />}
        {ogImage && <meta property="og:image:width" content="1200" />}
        {ogImage && <meta property="og:image:height" content="630" />}

        {/* Article-specific OG */}
        {article.publishedDate && (
          <meta property="article:published_time" content={article.publishedDate} />
        )}
        {article.modifiedDate && (
          <meta property="article:modified_time" content={article.modifiedDate} />
        )}
        {author?.name && <meta property="article:author" content={author.name} />}
        {article.tags?.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={description} />
        {twitterImage && <meta name="twitter:image" content={twitterImage} />}
        {twitterImage && <meta name="twitter:image:alt" content={ogImageAlt} />}
      </Head>

      <ArticleJsonLd
        type="Blog"
        url={canonical}
        title={title}
        images={ogImage ? [ogImage] : []}
        datePublished={article.publishedDate}
        dateModified={article.modifiedDate || article.publishedDate}
        authorName={[{
          name: author.name,
          url: `${SITE_URL}/author/${author.id}`,
        }]}
        publisherName={STOREFRONT_NAME}
        publisherLogo={`${SITE_URL}/static/og-image-square-small.png`}
        description={description}
        wordCount={articleWordCount}
        keywords={article.tags?.join(', ')}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Home', item: SITE_URL },
          { position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { position: 3, name: title, item: canonical },
        ]}
      />
      {faqBlock && (
        <FAQPageJsonLd
          mainEntity={faqBlock.questions.map((q) => ({
            questionName: q.question,
            acceptedAnswerText: q.answer,
          }))}
        />
      )}
    </>
  )
}
